import { createElement, Fragment, useMemo, type CSSProperties, type ReactNode } from 'react';

const allowed = new Set(['p', 'div', 'span', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'ul', 'ol', 'li', 'blockquote', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6']);
const omit = new Set(['script', 'style', 'iframe', 'object', 'embed', 'svg', 'math', 'template', 'noscript', 'img', 'video', 'audio', 'form', 'input', 'button', 'link', 'meta', 'base']);
const blockClasses: Record<string, string> = {
 p: 'my-2 first:mt-0 last:mb-0', div: 'my-1 first:mt-0 last:mb-0',
 ul: 'my-2 list-disc space-y-1 pl-5', ol: 'my-2 list-decimal space-y-1 pl-5',
 blockquote: 'my-2 border-l-2 border-slate-300 pl-3',
 h1: 'my-2 font-semibold', h2: 'my-2 font-semibold', h3: 'my-2 font-semibold',
 h4: 'my-2 font-semibold', h5: 'my-2 font-semibold', h6: 'my-2 font-semibold',
};

function formattedDescription(description: string): ReactNode {
 // Parse only in a detached template. Never insert source nodes/HTML into the app.
 // Rebuild allowed formatting as React elements; source attributes are discarded.
 const template = document.createElement('template');
 template.innerHTML = description;
 function renderNode(node: Node, key: number, depth = 0): ReactNode {
  if (node.nodeType === 3) return node.textContent;
  if (node.nodeType !== 1 || depth > 80) return null;
  const element = node as HTMLElement, tag = element.localName;
  if (omit.has(tag) || element.classList.contains('ql-ui')) return null;
  const children = Array.from(element.childNodes).map((child, index) => renderNode(child, index, depth + 1));
  if (!allowed.has(tag)) return createElement(Fragment, { key }, ...children);
  const style: CSSProperties = {};
  const indent = element.className.match(/(?:^|\s)ql-indent-([1-8])(?:\s|$)/)?.[1];
  if (indent) style.marginLeft = `${Number(indent) * 1.5}rem`;
  else {
   const margin = element.style.marginLeft || element.style.paddingLeft;
   if (/^\d+(?:\.\d+)?(?:px|em|rem)$/.test(margin) && parseFloat(margin) <= 240) style.marginLeft = margin;
  }
  const align = element.style.textAlign;
  if (['left', 'center', 'right', 'justify'].includes(align)) style.textAlign = align as CSSProperties['textAlign'];
  if (tag === 'li' && element.getAttribute('data-list') === 'bullet') style.listStyleType = 'disc';
  if (tag === 'li' && element.getAttribute('data-list') === 'ordered') style.listStyleType = 'decimal';
  const classes = [blockClasses[tag], ['strong', 'b'].includes(tag) ? 'font-semibold' : '', ['em', 'i'].includes(tag) ? 'italic' : '', tag === 'u' ? 'underline' : '', tag === 's' ? 'line-through' : ''].filter(Boolean).join(' ');
  return createElement(tag, { key, className: classes || undefined, style }, ...(tag === 'br' ? [] : children));
 }
 return Array.from(template.content.childNodes).map((node, index) => renderNode(node, index));
}

export function PlanDescription({ description, className = '' }: { description: string; className?: string }) {
 const content = useMemo(() => formattedDescription(description), [description]);
 return <div className={`whitespace-pre-line break-words ${className}`}>{content}</div>;
}
